import {
  ArrowRight,
  BadgeCheck,
  BarChart3,
  Bot,
  BrainCircuit,
  Building2,
  Check,
  ChevronRight,
  ClipboardCheck,
  DatabaseZap,
  Factory,
  Gauge,
  Handshake,
  Layers3,
  Mail,
  Menu,
  MessageSquareText,
  Network,
  Rocket,
  Scale,
  ShieldCheck,
  Sparkles,
  Target,
  Users,
  X
} from "lucide-react";
import { type FormEvent, useEffect, useState } from "react";
import { saveContactLeadToCRM, trackEvent } from "./apteva";
import {
  content,
  localeFromPath,
  localeLabels,
  localePath,
  locales,
  stripLocaleFromPath,
  type Locale,
  type PageKey,
  type SiteContent
} from "./i18n";

export type { Locale, PageKey };

const serviceIcons = [Target, Bot, DatabaseZap, ShieldCheck];
const featuredCaseStudyIndexes = [0, 3, 4, 5];

export function pageFromPath(pathname: string): PageKey {
  const normalizedPath = stripLocaleFromPath(pathname);
  if (normalizedPath.startsWith("/services")) return "services";
  if (normalizedPath.startsWith("/case-studies")) return "case-studies";
  if (normalizedPath.startsWith("/strategy")) return "strategy";
  if (normalizedPath.startsWith("/tiers")) return "tiers";
  if (normalizedPath.startsWith("/contact")) return "contact";
  return "home";
}

export { localeFromPath };

function BrandMark() {
  return (
    <svg className="brand-symbol" viewBox="0 0 64 64" aria-hidden="true" focusable="false">
      <defs>
        <clipPath id="brand-focus-circle">
          <circle cx="32" cy="32" r="16" />
        </clipPath>
      </defs>
      <path className="brand-corner brand-corner-blue" d="M9 28V9h19" />
      <path className="brand-corner brand-corner-ink" d="M36 9h19v19" />
      <path className="brand-corner brand-corner-ink" d="M28 55H9V36" />
      <path className="brand-corner brand-corner-soft" d="M55 36v19H36" />
      <g clipPath="url(#brand-focus-circle)">
        <rect x="16" y="16" width="16" height="16" className="brand-quadrant brand-quadrant-blue" />
        <rect x="32" y="16" width="16" height="16" className="brand-quadrant brand-quadrant-blue-soft" />
        <rect x="16" y="32" width="16" height="16" className="brand-quadrant brand-quadrant-ink" />
        <rect x="32" y="32" width="16" height="16" className="brand-quadrant brand-quadrant-pale" />
      </g>
      <path className="brand-divider" d="M32 16v32M16 32h32" />
    </svg>
  );
}

export function App({ page = "home", locale = "en" }: { page?: PageKey; locale?: Locale }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const copy = content[locale];

  function rememberLocale(nextLocale: Locale) {
    if (typeof window === "undefined") return;
    window.localStorage.setItem("preferred_locale", nextLocale);
  }

  return (
    <div className="site-shell">
      <header className="topbar">
        <a className="brand" href={localePath(locale, "home")} aria-label={copy.ui.brandAria}>
          <span className="brand-mark">
            <BrandMark />
          </span>
          <span>
            <strong>Schwartz AI Partners</strong>
            <small>{copy.ui.brandSubline}</small>
          </span>
        </a>

        <nav className="desktop-nav" aria-label={copy.ui.navLabel}>
          {copy.nav.map((item) => (
            <a key={item.page} href={localePath(locale, item.page)} aria-current={page === item.page ? "page" : undefined}>
              {item.label}
            </a>
          ))}
        </nav>

        <nav className="language-switcher" aria-label={copy.ui.languageLabel}>
          {locales.map((item) => (
            <a
              key={item}
              href={localePath(item, page)}
              aria-current={locale === item ? "true" : undefined}
              onClick={() => rememberLocale(item)}
            >
              {localeLabels[item]}
            </a>
          ))}
        </nav>

        <a className="nav-cta" href={localePath(locale, "contact")}>
          {copy.ui.cta}
          <ChevronRight size={16} aria-hidden="true" />
        </a>

        <button
          className="menu-button"
          type="button"
          aria-label={menuOpen ? copy.ui.closeMenu : copy.ui.openMenu}
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((value) => !value)}
        >
          {menuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </header>

      {menuOpen ? (
        <nav className="mobile-nav" aria-label={copy.ui.mobileNavLabel}>
          {copy.nav.map((item) => (
            <a key={item.page} href={localePath(locale, item.page)} onClick={() => setMenuOpen(false)}>
              {item.label}
            </a>
          ))}
        </nav>
      ) : null}

      {page === "home" ? <HomePage content={copy} locale={locale} /> : null}
      {page === "services" ? <ServicesPage content={copy} locale={locale} /> : null}
      {page === "case-studies" ? <CaseStudiesPage content={copy} /> : null}
      {page === "strategy" ? <StrategyPage content={copy} /> : null}
      {page === "tiers" ? <TiersPage content={copy} locale={locale} /> : null}
      {page === "contact" ? <ContactPage content={copy} locale={locale} /> : null}

      <footer className="footer">
        <span>Schwartz AI Partners</span>
        <span>{copy.ui.footer}</span>
      </footer>
    </div>
  );
}

function HomePage({ content: copy, locale }: { content: SiteContent; locale: Locale }) {
  return (
    <main id="home">
      <section className="hero">
        <div className="hero-copy">
          <span className="eyebrow">{copy.home.eyebrow}</span>
          <h1 className="hero-title">{copy.home.title}</h1>
          <p className="hero-lede">{copy.home.lede}</p>
          <div className="hero-actions">
            <a className="primary-button" href={localePath(locale, "contact")}>
              {copy.home.primaryCta}
              <ArrowRight size={18} aria-hidden="true" />
            </a>
            <a className="secondary-button" href={localePath(locale, "case-studies")}>
              {copy.home.secondaryCta}
            </a>
          </div>
          <ul className="outcome-list" aria-label={copy.ui.outcomesLabel}>
            {copy.outcomes.map((item) => (
              <li key={item}>
                <Check size={17} aria-hidden="true" />
                {item}
              </li>
            ))}
          </ul>
        </div>

        <HeroVisual content={copy} />
      </section>

      <TrustBand content={copy} />
      <ServicesOverview content={copy} />
      <CaseStudyPreview content={copy} locale={locale} />
      <StrategyOverview content={copy} />
      <section className="lead-band">
        <div>
          <span className="section-kicker">{copy.home.leadKicker}</span>
          <h2>{copy.home.leadTitle}</h2>
          <p>{copy.home.leadText}</p>
        </div>
        <LeadForm content={copy} locale={locale} variant="compact" sourceSurface="home-inline" buttonText={copy.home.leadButton} />
      </section>
    </main>
  );
}

function ServicesPage({ content: copy, locale }: { content: SiteContent; locale: Locale }) {
  return (
    <main>
      <PageHero
        kicker={copy.servicesPage.kicker}
        title={copy.servicesPage.title}
        text={copy.servicesPage.text}
      />
      <ServicesGrid content={copy} detailed />
      <section className="split-section page-split">
        <div>
          <span className="section-kicker">{copy.servicesPage.deliveryKicker}</span>
          <h2>{copy.servicesPage.deliveryTitle}</h2>
          <p>{copy.servicesPage.deliveryText}</p>
        </div>
        <div className="use-case-list">
          {copy.servicesPage.useCases.map((item) => (
            <div className="use-case" key={item}>
              <BadgeCheck size={20} aria-hidden="true" />
              <span>{item}</span>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
}

function CaseStudiesPage({ content: copy }: { content: SiteContent }) {
  return (
    <main>
      <PageHero
        kicker={copy.caseStudiesPage.kicker}
        title={copy.caseStudiesPage.title}
        text={copy.caseStudiesPage.text}
      />
      <CaseStudyGrid content={copy} />
      <section className="section proof-section">
        <div className="section-heading">
          <span className="section-kicker">{copy.caseStudiesPage.proofKicker}</span>
          <h2>{copy.caseStudiesPage.proofTitle}</h2>
          <p>{copy.caseStudiesPage.proofText}</p>
        </div>
        <div className="proof-grid">
          {copy.caseStudiesPage.proofCards.map(([title, text]) => (
            <article className="proof-card" key={title}>
              <ShieldCheck size={22} aria-hidden="true" />
              <h3>{title}</h3>
              <p>{text}</p>
            </article>
          ))}
        </div>
      </section>
    </main>
  );
}

function StrategyPage({ content: copy }: { content: SiteContent }) {
  return (
    <main>
      <PageHero
        kicker={copy.strategyPage.kicker}
        title={copy.strategyPage.title}
        text={copy.strategyPage.text}
      />
      <StrategyDetail content={copy} />
      <section className="section expertise-depth">
        <div className="section-heading">
          <span className="section-kicker">{copy.strategyPage.winsKicker}</span>
          <h2>{copy.strategyPage.winsTitle}</h2>
          <p>{copy.strategyPage.winsText}</p>
        </div>
        <div className="expertise-depth-grid">
          {copy.strategyPage.wins.map((item) => (
            <div className="use-case" key={item}>
              <Check size={20} aria-hidden="true" />
              <span>{item}</span>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
}

function TiersPage({ content: copy, locale }: { content: SiteContent; locale: Locale }) {
  return (
    <main>
      <PageHero
        kicker={copy.tiersPage.kicker}
        title={copy.tiersPage.title}
        text={copy.tiersPage.text}
      />
      <TierCards content={copy} locale={locale} />
      <section className="lead-band lead-band-dark">
        <div>
          <span className="section-kicker">{copy.tiersPage.fitKicker}</span>
          <h2>{copy.tiersPage.fitTitle}</h2>
          <p>{copy.tiersPage.fitText}</p>
        </div>
        <LeadForm content={copy} locale={locale} variant="compact" sourceSurface="tiers-inline" includePlan buttonText={copy.tiersPage.fitButton} />
      </section>
      <FAQSection content={copy} />
    </main>
  );
}

function ContactPage({ content: copy, locale }: { content: SiteContent; locale: Locale }) {
  return (
    <main>
      <PageHero
        kicker={copy.contactPage.kicker}
        title={copy.contactPage.title}
        text={copy.contactPage.text}
      />
      <section className="contact-direct">
        <div>
          <span className="section-kicker">{copy.contactPage.intakeKicker}</span>
          <h2>{copy.contactPage.intakeTitle}</h2>
          <p>{copy.contactPage.intakeText}</p>
          <div className="contact-options">
            <a href="mailto:contact@schwartzaipartners.com">
              <Mail size={18} aria-hidden="true" />
              contact@schwartzaipartners.com
            </a>
            <a href={localePath(locale, "tiers")}>
              <Layers3 size={18} aria-hidden="true" />
              {copy.contactPage.compareTiers}
            </a>
          </div>
        </div>
        <LeadForm content={copy} locale={locale} variant="full" sourceSurface="contact-page" includePlan buttonText={copy.contactPage.button} />
      </section>
    </main>
  );
}

function PageHero({ kicker, title, text }: { kicker: string; title: string; text: string }) {
  return (
    <section className="page-hero">
      <span className="section-kicker">{kicker}</span>
      <h1>{title}</h1>
      <p>{text}</p>
    </section>
  );
}

function HeroVisual({ content: copy }: { content: SiteContent }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const featuredCaseStudies = featuredCaseStudyIndexes.map((index) => copy.caseStudies[index]);
  const activeCase = featuredCaseStudies[activeIndex];

  return (
    <div className="hero-visual consulting-command" aria-label={copy.heroVisual.aria}>
      <div className="command-header">
        <div>
          <span>{copy.heroVisual.cockpit}</span>
          <strong>{activeCase.title}</strong>
        </div>
        <span className="command-status">{copy.heroVisual.status}</span>
      </div>

      <div className="command-tabs" role="tablist" aria-label={copy.heroVisual.tabsAria}>
        {featuredCaseStudies.map((item, index) => (
          <button
            key={item.sector}
            type="button"
            role="tab"
            aria-selected={activeIndex === index}
            className={activeIndex === index ? "active" : undefined}
            onClick={() => {
              setActiveIndex(index);
              trackEvent("ai_case_example_changed", { sector: item.sector });
            }}
          >
            {item.sector}
          </button>
        ))}
      </div>

      <div className="command-panel">
        <div className="command-map">
          <div className="command-node command-node-dark">
            <Building2 size={19} aria-hidden="true" />
            <span>{copy.heroVisual.businessWorkflow}</span>
          </div>
          <ArrowRight size={18} aria-hidden="true" />
          <div className="command-node">
            <BrainCircuit size={19} aria-hidden="true" />
            <span>{copy.heroVisual.aiAgent}</span>
          </div>
          <ArrowRight size={18} aria-hidden="true" />
          <div className="command-node">
            <Users size={19} aria-hidden="true" />
            <span>{copy.heroVisual.humanApproval}</span>
          </div>
        </div>

        <div className="case-snapshot">
          <span>{activeCase.sector}</span>
          <h3>{activeCase.result}</h3>
          <p>{activeCase.text}</p>
        </div>

        <div className="impact-grid" aria-label={copy.ui.impactLabel}>
          {activeCase.metrics.map((metric) => (
            <div key={metric}>
              <span>{copy.heroVisual.signal}</span>
              <strong>{metric}</strong>
            </div>
          ))}
        </div>
      </div>

      <div className="agent-result">
        <ShieldCheck size={18} aria-hidden="true" />
        {copy.heroVisual.result}
      </div>
    </div>
  );
}

function TrustBand({ content: copy }: { content: SiteContent }) {
  return (
    <section className="trust-band" aria-label={copy.ui.trustSignalsLabel}>
      {copy.trustSignals.map((item) => (
        <div key={item.label}>
          <strong>{item.value}</strong>
          <span>{item.label}</span>
        </div>
      ))}
    </section>
  );
}

function ServicesOverview({ content: copy }: { content: SiteContent }) {
  return (
    <section className="section" id="services">
      <div className="section-heading">
        <span className="section-kicker">{copy.sections.consultingServices}</span>
        <h2>{copy.sections.ambitionTitle}</h2>
        <p>{copy.sections.ambitionText}</p>
      </div>
      <ServicesGrid content={copy} />
    </section>
  );
}

function ServicesGrid({ content: copy, detailed = false }: { content: SiteContent; detailed?: boolean }) {
  return (
    <section className={detailed ? "section" : undefined}>
      <div className="service-grid">
        {copy.services.map((service, index) => {
          const ServiceIcon = serviceIcons[index] || Target;
          return (
            <article className="service-card service-card-detailed" key={service.title}>
              <ServiceIcon size={28} aria-hidden="true" />
              <h3>{service.title}</h3>
              <p>{service.text}</p>
              {detailed ? (
                <ul className="compact-list">
                  {service.points.map((point) => (
                    <li key={point}>{point}</li>
                  ))}
                </ul>
              ) : null}
            </article>
          );
        })}
      </div>
    </section>
  );
}

function CaseStudyPreview({ content: copy, locale }: { content: SiteContent; locale: Locale }) {
  return (
    <section className="split-section" id="case-studies">
      <div>
        <span className="section-kicker">{copy.sections.caseStudiesKicker}</span>
        <h2>{copy.sections.caseStudiesTitle}</h2>
        <p>{copy.sections.caseStudiesText}</p>
        <a className="text-link" href={localePath(locale, "case-studies")}>
          {copy.sections.exploreCaseStudies}
          <ArrowRight size={17} aria-hidden="true" />
        </a>
      </div>
      <div className="use-case-list">
        {copy.caseStudies.map((item) => (
          <div className="use-case" key={item.title}>
            <BarChart3 size={20} aria-hidden="true" />
            <span>
              {item.sector}: {item.result}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}

function CaseStudyGrid({ content: copy }: { content: SiteContent }) {
  return (
    <section className="section case-grid-section">
      <div className="case-grid">
        {copy.caseStudies.map((item) => (
          <article className="case-card" key={item.title}>
            <span>{item.sector}</span>
            <h2>{item.title}</h2>
            <strong>{item.result}</strong>
            <p>{item.text}</p>
            <ul>
              {item.metrics.map((metric) => (
                <li key={metric}>
                  <Check size={17} aria-hidden="true" />
                  {metric}
                </li>
              ))}
            </ul>
          </article>
        ))}
      </div>
    </section>
  );
}

function StrategyOverview({ content: copy }: { content: SiteContent }) {
  return (
    <section className="section method-section" id="strategy">
      <div className="section-heading">
        <span className="section-kicker">{copy.sections.methodKicker}</span>
        <h2>{copy.sections.methodTitle}</h2>
      </div>
      <div className="timeline">
        {copy.strategySteps.map((item) => (
          <article className="timeline-item" key={item.step}>
            <span>{item.step}</span>
            <h3>{item.title}</h3>
            <p>{item.text}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

function StrategyDetail({ content: copy }: { content: SiteContent }) {
  return (
    <section className="section method-detail-section">
      <div className="method-detail-list">
        {copy.strategySteps.map((item) => (
          <article className="method-detail-card" key={item.step}>
            <span>{item.step}</span>
            <div>
              <h2>{item.title}</h2>
              <p>{item.text}</p>
              <strong>{item.output}</strong>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function TierCards({ content: copy, locale }: { content: SiteContent; locale: Locale }) {
  return (
    <section className="section pricing-section">
      <div className="pricing-grid">
        {copy.serviceTiers.map((tier) => (
          <article className={`pricing-card ${tier.featured ? "pricing-card-featured" : ""}`} key={tier.name}>
            <div className="pricing-card-head">
              <span className={`pricing-badge ${tier.featured ? "" : "pricing-badge-placeholder"}`}>
                {tier.featured ? copy.tiersPage.mostPopular : copy.tiersPage.serviceTier}
              </span>
              <h2>{tier.name}</h2>
              <p>{tier.description}</p>
            </div>
            <div className="price-line">
              <strong>{tier.price}</strong>
              <span>{tier.monthly}</span>
            </div>
            <ul>
              {tier.features.map((feature) => (
                <li key={feature}>
                  <Check size={18} aria-hidden="true" />
                  {feature}
                </li>
              ))}
            </ul>
            <a className={`${tier.featured ? "primary-button" : "secondary-button"} pricing-card-button`} href={`${localePath(locale, "contact")}?plan=${encodeURIComponent(tier.name)}`}>
              {copy.tiersPage.discuss} {tier.name}
              <ArrowRight size={17} aria-hidden="true" />
            </a>
          </article>
        ))}
      </div>
    </section>
  );
}

function FAQSection({ content: copy }: { content: SiteContent }) {
  return (
    <section className="section faq-section">
      <div className="section-heading">
        <span className="section-kicker">{copy.sections.questionsKicker}</span>
        <h2>{copy.sections.questionsTitle}</h2>
      </div>
      <div className="faq-grid">
        {copy.faqItems.map(([question, answer]) => (
          <article className="faq-card" key={question}>
            <h3>{question}</h3>
            <p>{answer}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

function LeadForm({
  content: copy,
  locale,
  variant,
  sourceSurface,
  includePlan = false,
  buttonText
}: {
  content: SiteContent;
  locale: Locale;
  variant: "compact" | "full";
  sourceSurface: string;
  includePlan?: boolean;
  buttonText: string;
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<"idle" | "success" | "error">("idle");
  const [hasFocused, setHasFocused] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("");

  useEffect(() => {
    if (!includePlan || typeof window === "undefined") return;
    const plan = new URLSearchParams(window.location.search).get("plan") || "";
    if (copy.serviceTiers.some((tier) => tier.name === plan)) setSelectedPlan(plan);
  }, [copy.serviceTiers, includePlan]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);
    if (String(formData.get("_honey") || "").trim()) return;

    setIsSubmitting(true);
    setSubmitStatus("idle");

    const payload = {
      name: String(formData.get("name") || ""),
      email: String(formData.get("email") || ""),
      company: String(formData.get("company") || ""),
      phone: String(formData.get("phone") || ""),
      companySize: String(formData.get("company_size") || ""),
      priorityNeed: String(formData.get("priority_need") || ""),
      message: String(formData.get("message") || ""),
      industry: String(formData.get("industry") || ""),
      plan: String(formData.get("plan") || ""),
      preferredContact: String(formData.get("preferred_contact") || ""),
      projectTimeline: String(formData.get("timeline") || ""),
      sourceSurface,
      locale,
      pagePath: typeof window === "undefined" ? "/contact/" : window.location.pathname
    };

    trackEvent("ai_consulting_lead_submit", {
      source_surface: sourceSurface,
      industry: payload.industry,
      plan: payload.plan,
      preferred_contact: payload.preferredContact,
      locale: payload.locale
    });

    try {
      await saveContactLeadToCRM(payload);
      trackEvent("ai_consulting_lead_success", {
        source_surface: sourceSurface,
        industry: payload.industry,
        plan: payload.plan,
        locale: payload.locale
      });
      setSubmitStatus("success");
      form.reset();
      setSelectedPlan("");
      setHasFocused(false);
    } catch (error) {
      console.error("Lead request failed:", error);
      trackEvent("ai_consulting_lead_error", {
        source_surface: sourceSurface,
        industry: payload.industry,
        plan: payload.plan,
        locale: payload.locale
      });
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleFormFocus() {
    if (hasFocused) return;
    setHasFocused(true);
    trackEvent("ai_consulting_lead_started", {
      source_surface: sourceSurface,
      page_path: typeof window === "undefined" ? "/contact/" : window.location.pathname
    });
  }

  return (
    <form className={`contact-form lead-form-${variant}`} onSubmit={handleSubmit} onFocusCapture={handleFormFocus}>
      <input className="form-honey" type="text" name="_honey" tabIndex={-1} autoComplete="off" aria-hidden="true" />

      <label>
        {copy.form.name}
        <input name="name" type="text" autoComplete="name" required />
      </label>

      <label>
        {copy.form.email}
        <input name="email" type="email" autoComplete="email" required />
      </label>

      <label>
        {copy.form.company}
        <input name="company" type="text" autoComplete="organization" required />
      </label>

      <label>
        {copy.form.phone}
        <input name="phone" type="tel" autoComplete="tel" />
      </label>

      <label>
        {copy.form.industry}
        <select name="industry" required defaultValue="">
          <option value="" disabled>
            {copy.form.select}
          </option>
          {copy.form.industries.map((item) => (
            <option value={item} key={item}>{item}</option>
          ))}
        </select>
      </label>

      <label>
        {copy.form.teamSize}
        <select name="company_size" defaultValue="">
          <option value="">{copy.form.select}</option>
          <option value="1-10">1-10</option>
          <option value="11-50">11-50</option>
          <option value="51-200">51-200</option>
          <option value="201-500">201-500</option>
          <option value="500+">500+</option>
        </select>
      </label>

      {includePlan ? (
        <label>
          {copy.form.serviceTier}
          <select name="plan" value={selectedPlan} onChange={(event) => setSelectedPlan(event.currentTarget.value)}>
            <option value="">{copy.form.recommendOne}</option>
            {copy.serviceTiers.map((tier) => (
              <option value={tier.name} key={tier.name}>
                {tier.name}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      <label>
        {copy.form.timeline}
        <select name="timeline" defaultValue="">
          <option value="">{copy.form.select}</option>
          {copy.form.timelines.map((item) => (
            <option value={item} key={item}>{item}</option>
          ))}
        </select>
      </label>

      <label>
        {copy.form.preferredContact}
        <select name="preferred_contact" defaultValue="">
          <option value="">{copy.form.any}</option>
          {copy.form.preferredContacts.map((item) => (
            <option value={item} key={item}>{item}</option>
          ))}
        </select>
      </label>

      <label className="full-field">
        {copy.form.improve}
        <textarea
          name="message"
          rows={variant === "full" ? 6 : 4}
          required
          placeholder={copy.form.improvePlaceholder}
        />
      </label>

      <input name="priority_need" type="hidden" value={copy.form.priorityNeed} readOnly />

      {submitStatus === "success" ? (
        <p className="form-alert form-alert-success full-field" role="status">
          {copy.form.success}
        </p>
      ) : null}

      {submitStatus === "error" ? (
        <p className="form-alert form-alert-error full-field" role="alert">
          {copy.form.error}
        </p>
      ) : null}

      <button className="primary-button full-field" type="submit" disabled={isSubmitting}>
        {isSubmitting ? copy.form.sending : buttonText}
        <ArrowRight size={18} aria-hidden="true" />
      </button>

      <p className="form-note">{copy.form.note}</p>
    </form>
  );
}
